import * as RadixAvatar from '@radix-ui/react-avatar';
import { createInitials } from '@visiba/std';
import { Fragment, forwardRef, ReactNode, Ref } from 'react';
import styled from 'styled-components';
import { coreTokens } from '../../core';
import { Icon, IconDictionary } from '../icon';
import { theme } from './avatar.theme';

type Size = 'small' | 'medium' | 'large';

interface PropsWithImage {
	/**
	 * Use to display image instead of icon/initials. Image url to the image.
	 */
	src?: string | null;

	/**
	 * The name of the person behind the avatar. If no `image` or `icon` is
	 * given. It will display the initials of the name enterd here.
	 */
	name: string;

	/**
	 * Use to add a icon in the avatar. If no `name` is given, this icon will be
	 * shown.
	 */
	icon?: keyof IconDictionary;
}

interface PropsWithIcon {
	src?: never;
	name?: never | null;
	icon: keyof IconDictionary;
}

type AvatarProps = {
	/**
	 * The visual size of the avatar.
	 * @default medium
	 */
	size?: Size;
} & (PropsWithImage | PropsWithIcon);

const elements = {
	root: styled(RadixAvatar.Root)`
		display: inline-flex;
		align-items: center;
		justify-content: center;
		vertical-align: middle;
		overflow: hidden;
		user-select: none;
		border-radius: 100%;
		background-color: ${theme.getValue('background')};
		flex-shrink: 0;

		&[data-size='small'] {
			--_avatar-size: 32px;
			--_font-size: ${coreTokens.text.label.semiBold};
		}
		&[data-size='medium'] {
			--_avatar-size: 40px;
			--_font-size: ${coreTokens.text.label.largeSemiBold};
		}
		&[data-size='large'] {
			--_avatar-size: 48px;
			--_font-size: ${coreTokens.text.label.xLargeSemiBold};
		}

		width: var(--_avatar-size);
		height: var(--_avatar-size);
	`,
	image: styled(RadixAvatar.Image)`
		width: 100%;
		height: 100%;
		object-fit: cover;
		border-radius: inherit;
	`,
	icon: styled.div`
		width: 100%;
		height: 100%;
		object-fit: cover;
		border-radius: inherit;
		display: flex;
		justify-content: center;
		align-items: center;
		background-color: ${theme.getValue('iconBackground')};
		color: ${theme.getValue('iconColor')};
		font-size: var(--_avatar-size);
	`,
	fallback: styled(RadixAvatar.Fallback)`
		width: 100%;
		height: 100%;
		display: flex;
		align-items: center;
		justify-content: center;
		background-color: ${theme.getValue('fallbackBackground')};
		color: ${theme.getValue('fallbackColor')};
		font: var(--_font-size);
	`,
};

function CellulaAvatar({ src, name, size = 'medium', icon }: AvatarProps, ref: Ref<HTMLSpanElement>): JSX.Element {
	return (
		<elements.root data-size={size} ref={ref}>
			<IllustrativeContent src={src} fallback={name} icon={icon != null ? <Icon type={icon} size={size} /> : null} />
		</elements.root>
	);
}

interface IllustrativePropsWithSrc {
	src: string;
	fallback: string;
	icon?: never;
}

interface IllustrativePropsWithIcon {
	src?: string | null;
	fallback?: string | null;
	icon: ReactNode;
}

type IllustrativeProps = IllustrativePropsWithSrc | IllustrativePropsWithIcon;

function IllustrativeContent({ fallback, src, icon }: IllustrativeProps): JSX.Element {
	if (fallback == null) {
		return <elements.icon aria-hidden='true'>{icon}</elements.icon>;
	}

	const initials = createInitials(fallback ?? '', '');

	return (
		<Fragment>
			{src != null ? <elements.image aria-hidden='true' src={src} /> : null}
			<elements.fallback aria-hidden='true' delayMs={src ? 600 : undefined}>
				{initials}
			</elements.fallback>
		</Fragment>
	);
}

/* ########################################################################## */
/* ############################### PUBLIC API ############################### */
/* ########################################################################## */

/**
 * A component for displaying a avatar.
 */
const Avatar = forwardRef(CellulaAvatar);

export type { AvatarProps };
export { Avatar };
